import React, { useRef } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { updateCard, updateExtraByName } from 'apps/auth/actions/extraAction';
import { addNewFields, changeFormField, setNewFields, updateFields } from 'apps/auth/actions/formAction';
import { setRequestDeliveryMethod } from 'apps/auth/actions/requestAction';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import DeleteCardPopup from 'apps/auth/components/common/popups/DeleteCardPopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { showSourceInfo } from 'apps/auth/components/form-multi/utils';
import { REQUESTS_PAYMENT_METHOD_SELECTED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_METHOD_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { requestPaymentMethods } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import {
    BONUS,
    DC,
    EXTERNAL,
    PAYMENT_GLOBAL_ACCOUNT,
    ACCOUNT,
    CARD,
    DELIVERY_BANK_ACCOUNT,
    DELIVERY_BANK_CARD,
    PAYMENT_BANK_CARD,
    PAYMENT_BONUS,
} from 'apps/auth/constants/Constants';
import { SOURCES_FROM, SOURCES_TO_BANK_CARD } from 'apps/auth/constants/ExtraNames';
import {
    ACCOUNT_TO,
    AMOUNT_TO,
    CARD_EXP_DATE,
    DELIVERY_METHOD,
    FROM_ACCOUNT,
    FROM_PRV_ID,
    PAYMENT_METHOD,
    SOURCE,
    SOURCE_CARD_CVV,
    SOURCE_CARD_EXP_DATE,
    SOURCE_CODE_FROM,
    TO_ACCOUNT_TYPE,
    TO_COMMENT,
    TO_RECIPIENT,
    TO_SORT_CODE,
} from 'apps/auth/constants/fieldsName';
import { REQUEST_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_REQUEST_CREATE } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import update from 'apps/auth/helpers/Immutability';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useFields, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import AddEmptySource from './components/AddEmptySource';
import AddSource from './components/AddSource';
import DeliveryMethodLibre from './components/libre/DeliveryMethodLibre';
import SourcesList from './components/SourcesList';
import { getField } from '../form-multi/utils';

/**
 * TODO Refactor work with sources from (need to make like Send flow PaymentMethods)
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const PaymentMethodScreen = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const popupRef = useRef(null);
    const { canTransferToCard, sourceToLibre, commission } = useExtra();
    const { selectHowToReceive, payRequestPayTitle, linkedCardsTitle } = usePageMessages();
    const fields = useFields(props.formName);
    const isEmptyCards = Object.keys(props.sourcesCards)
        .filter((a) => !a.includes(BONUS))
        .every((a) => a.includes(EXTERNAL));

    const amplitudePaymentMethodEvent = (method) => {
        amplitude.getInstance().logEvent(REQUESTS_PAYMENT_METHOD_SELECTED, {
            [PAYMENT_METHOD_AMPLITUDE]: method,
        });
    };

    const clickHandler = (item) => {
        if (item.type === ACCOUNT) {
            amplitudePaymentMethodEvent(requestPaymentMethods.ACCOUNT);
            props.setIsNewAccount(false);
            props.setRequestDeliveryMethod(DELIVERY_BANK_ACCOUNT).then(() => selectAccount(item));
        } else if (item.type === CARD) {
            amplitudePaymentMethodEvent(requestPaymentMethods.CARD);
            props.setRequestDeliveryMethod(DELIVERY_BANK_CARD).then(() => selectCard(item));
        } else if (item.type === '1001' || item.type === '10') {
            const newFields = createPayRequestFields(item, PAYMENT_BANK_CARD);
            props.updateFields(props.formName, newFields).then(() => props.selectedCard?.(item, newFields, PAYMENT_BANK_CARD));
        } else if (item.sourceType === BONUS) {
            const newFields = createPayRequestFields(item, PAYMENT_BONUS);
            props.updateFields(props.formName, newFields).then(() => props.selectedBonus(newFields, PAYMENT_BONUS));
        } else if (item.type === '1006') {
            const newFields = createPayRequestFields(item, PAYMENT_GLOBAL_ACCOUNT);
            props.updateFields(props.formName, newFields).then(() => props.selectedGAccount(newFields, PAYMENT_GLOBAL_ACCOUNT));
        }
    };

    const updateCardFields = (card, responseFields) => {
        if (props.isCreate) {
            const newFields = responseFields || fields;
            const fieldExpDate = { ...newFields[CARD_EXP_DATE] };
            const fieldSource = { ...newFields[SOURCE] };
            const fieldSourceCodeFrom = { ...newFields[SOURCE_CODE_FROM] };
            const fieldAccountTo = { ...newFields[ACCOUNT_TO] };
            if (card) {
                fieldSource.value = card.code;
                fieldSource.isError = false;

                fieldSourceCodeFrom.value = card.code;
                fieldSourceCodeFrom.isError = false;

                fieldAccountTo.value = card.pan;
                fieldAccountTo.isError = false;

                fieldExpDate.value = card.cardExpired;
                fieldExpDate.isError = false;
            } else {
                fieldSource.value = null;
                fieldSource.isError = false;

                fieldSourceCodeFrom.value = `type=${EXTERNAL}`;
                fieldSourceCodeFrom.isError = false;

                fieldAccountTo.value = null;
                fieldAccountTo.isError = false;

                fieldExpDate.value = null;
                fieldExpDate.isError = false;
            }
            return Promise.resolve(props.updateFields(props.formName, [fieldSource, fieldAccountTo, fieldExpDate, fieldSourceCodeFrom]));
        } else {
            const newFields = createPayRequestFields({}, PAYMENT_BANK_CARD);
            return Promise.resolve(props.updateFields(props.formName, newFields));
        }
    };

    const updateAccountFields = (account) => {
        if (account) {
            const fieldSourceCodeFrom = { ...fields[SOURCE_CODE_FROM] };
            const fieldSource = { ...fields[SOURCE] };
            const fieldAccountTo = { ...fields[ACCOUNT_TO] };
            fieldSource.value = account.code;
            fieldSource.isError = false;

            fieldSourceCodeFrom.value = account.code;
            fieldSourceCodeFrom.isError = false;

            fieldAccountTo.value = account.account;
            fieldAccountTo.isError = false;

            if (account.fieldsJson) {
                const reg = /'/g;
                const replaceJson = account.fieldsJson.replace(reg, '"');
                const itemJsonFields = JSON.parse(replaceJson);
                let newFields = {};
                Object.keys(itemJsonFields).forEach((key) => {
                    if (!fields[key]) {
                        newFields[key] = getField(key, itemJsonFields[key], '', true, false, '', 'text', false);
                    } else {
                        const newField = { ...fields[key] };
                        newField.value = itemJsonFields[key];
                        newField.visible = false;
                        props.changeFormField(props.formName, newField);
                    }
                });
                if (Object.keys(newFields).length > 0) {
                    props.addNewFields(props.formName, newFields);
                } else if (account.type === EXTERNAL && fields[TO_RECIPIENT]?.value) {
                    props.changeFormFieldValue(props.formName, '', TO_RECIPIENT);
                }
            }
            return Promise.resolve(props.updateFields(props.formName, [fieldAccountTo, fieldSourceCodeFrom, fieldSource]));
        } else {
            const newFields = {
                ...fields,
                [ACCOUNT_TO]: {
                    ...fields[ACCOUNT_TO],
                    value: null,
                    isError: false,
                },
                [TO_ACCOUNT_TYPE]: {
                    ...fields[TO_ACCOUNT_TYPE],
                    value: null,
                    isError: false,
                },
                [TO_SORT_CODE]: {
                    ...fields[TO_SORT_CODE],
                    value: null,
                    isError: false,
                },
                [TO_RECIPIENT]: {
                    ...fields[TO_RECIPIENT],
                    value: null,
                    isError: false,
                },
                [TO_COMMENT]: {
                    ...fields[TO_COMMENT],
                    value: null,
                    isError: false,
                },
            };
            return Promise.resolve(props.updateFields(props.formName, newFields));
        }
    };

    const requestFields = (card) => {
        props.toggleSpinner(true);
        apiFetchData(AUTH_REQUEST_CREATE, { ...fields, [DELIVERY_METHOD]: { ...fields[DELIVERY_METHOD], value: DELIVERY_BANK_CARD } })
            .then((json) => {
                const newFields = json?.forms[REQUEST_FORM_NAME]?.fields;
                props.setNewFields(REQUEST_FORM_NAME, newFields).then(() => {
                    updateCardFields(card, newFields).then(() => {
                        props.addCard();
                    });
                });
            })
            .catch((error) => {
                logWarn(error);
            })
            .finally(() => props.toggleSpinner(false));
    };

    const selectCard = (card) => {
        const prevDeliveryMethod = fields?.[DELIVERY_METHOD].value;
        if (props.isCreate && prevDeliveryMethod !== DELIVERY_BANK_CARD) {
            requestFields(card);
        } else {
            updateCardFields(card).then(() => {
                props.addCard();
            });
        }
    };

    const createPayRequestFields = (item = {}, paymentMethod = PAYMENT_BANK_CARD) => {
        const isCard = paymentMethod === PAYMENT_BANK_CARD;
        const source = item?.code || null;
        const fromPrvId = item?.fromPrvId || null;
        const account = item?.account || null;
        const expDate = item?.cardExpired || null;
        return {
            ...fields,
            [SOURCE]: {
                ...fields[SOURCE],
                value: source,
                isError: false,
            },
            [SOURCE_CODE_FROM]: {
                ...fields[SOURCE_CODE_FROM],
                value: source,
                isError: false,
            },
            [FROM_ACCOUNT]: {
                ...fields[FROM_ACCOUNT],
                value: account,
                isError: false,
                mandatory: isCard,
            },
            [SOURCE_CARD_CVV]: {
                ...fields[SOURCE_CARD_CVV],
                value: null,
                isError: false,
                mandatory: isCard,
            },
            [SOURCE_CARD_EXP_DATE]: {
                ...fields[SOURCE_CARD_EXP_DATE],
                value: expDate,
                isError: false,
                mandatory: isCard,
            },
            [PAYMENT_METHOD]: {
                ...fields[PAYMENT_METHOD],
                value: paymentMethod,
                isError: false,
            },
            [FROM_PRV_ID]: {
                ...fields[FROM_PRV_ID],
                value: fromPrvId,
                mandatory: false,
                isError: false,
            },
        };
    };

    const selectAccount = (item) => {
        updateAccountFields(item).then(() => props.addAccount());
    };

    const addEmptySource = (sources = {}, isAccount = false, onClickHandler = {}) => {
        return Object.keys(sources).map((key) => {
            const item = sources[key];
            if (key.indexOf(EXTERNAL) >= 0 && showSourceInfo(item)) {
                return (
                    <AddEmptySource
                        source={item}
                        key={key}
                        isAccount={isAccount}
                        onClick={() => (isAccount ? updateAccountFields() : updateCardFields()).then(() => onClickHandler(item))}
                        isCreate={props.isCreate}
                        amplitudePaymentMethodEvent={amplitudePaymentMethodEvent}
                        setIsNewAccount={props.setIsNewAccount}
                    />
                );
            }
        });
    };

    const addSource = (sources = {}, onClickHandler = {}, isAccount = false) => {
        return Object.keys(sources).map((key) => {
            const item = sources[key];
            if (key.indexOf(EXTERNAL) >= 0 && showSourceInfo(item)) {
                return (
                    <AddSource
                        source={item}
                        key={key}
                        onClick={() => (isAccount ? updateAccountFields() : updateCardFields()).then(() => onClickHandler(item))}
                        isAccount={isAccount}
                        amplitudePaymentMethodEvent={amplitudePaymentMethodEvent}
                        setIsNewAccount={props.setIsNewAccount}
                        iconName="card-plus"
                        variant="secondary"
                    />
                );
            }
        });
    };

    const getSourceList = (sources, sourceCode) => {
        if (sources) {
            return Object.keys(sources).map((key) => {
                const item = sources[key];
                if (key.indexOf(DC) > 0) {
                    return (
                        <SourcesList
                            key={key}
                            isSelected={key === sourceCode}
                            source={item}
                            onClick={() => clickHandler(item)}
                            onExpiredCardClick={() =>
                                popupRef.current?.open(
                                    <DeleteCardPopup
                                        popupRef={popupRef}
                                        selectedCard={item}
                                        customDeleteCard={(id) => {
                                            const source = Object.values(sources).find((s) => s.id === id);
                                            const newSources = update(sources, { $unset: [source.code] });
                                            const extraName = props.isCreate ? SOURCES_TO_BANK_CARD : SOURCES_FROM;
                                            return props.updateExtraByName(extraName, newSources);
                                        }}
                                    />,
                                )
                            }
                        />
                    );
                }
            });
        } else {
            return false;
        }
    };

    const getFormattedAmountTo = (value) => {
        return getLocalizedRawAmount(lang, country, value);
    };

    return (
        <>
            <HeaderPanel
                title={resolveArguments(
                    props.isCreate ? selectHowToReceive : payRequestPayTitle,
                    `${getFormattedAmountTo(fields[AMOUNT_TO]?.value)} ${commission?.restrictionTo?.ccyIso}`,
                )}
            />
            {canTransferToCard &&
                (isEmptyCards ? (
                    <>{addEmptySource(props.sourcesCards, false, props.addCard)}</>
                ) : (
                    <>
                        <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                            {linkedCardsTitle}
                        </Text>
                        {getSourceList(props.sourcesCards, props.sourceCode)}
                        {addSource(props.sourcesCards, props.addCard, false)}
                    </>
                ))}
            {/*https://paysend.atlassian.net/browse/PBTC-5122*/}
            {/*{canTransferToBankAccount &&*/}
            {/*    props.isCreate &&*/}
            {/*    (isEmptyAccounts ? (*/}
            {/*        <>*/}
            {/*            {!isEmptyCards && <Separator marginBottom="48px" />}*/}
            {/*            {addEmptySource(props.sourcesAccounts, true, props.addAccount)}*/}
            {/*        </>*/}
            {/*    ) : (*/}
            {/*        <>*/}
            {/*            <Separator marginBottom="48px" />*/}
            {/*            <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">*/}
            {/*                {linkedAccountsTitle}*/}
            {/*            </Text>*/}
            {/*            {addSource(props.sourcesAccounts, props.addAccount, true)}*/}
            {/*            {getSourceList(props.sourcesAccounts, props.sourceCode)}*/}
            {/*        </>*/}
            {/*    ))}*/}
            {props.isCreate && sourceToLibre && <DeliveryMethodLibre chooseLibre={props.chooseLibre} sourceToLibre={sourceToLibre} />}
            {/*{!props.isCreate && <>{getBonusMethod(props.sourcesBonus, props.sourceCode, props.amountValue)}</>}*/}
            {/*{!props.isCreate && <>{getGAMethod(props.sourcesGA, props.sourceCode, props.amountValue)}</>}*/}
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, {
    changeFormField,
    updateExtraByName,
    updateCard,
    addNewFields,
    updateFields,
    setRequestDeliveryMethod,
    toggleSpinner,
    setNewFields,
})(PaymentMethodScreen);
